import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import CarRepairOutlinedIcon from "@mui/icons-material/CarRepairOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
// Shared presentational pieces for the /app/* dashboard mockup pages -- kept here instead of
// duplicated per page since Overview, Home Inventory, Services, Calendar, and Recipes all use the
// same pill/stat-tile/row visual language. Colors are this site's own indigo accent plus a
// deliberately green-free semantic set (accent/amber/rose) for status -- no teal/emerald anywhere,
// per direct instruction.
export const TONE_COLOR = {
    overdue: { bg: "rgba(244, 63, 94, 0.14)", fg: "#fb7185" },
    due: { bg: "rgba(245, 158, 11, 0.14)", fg: "#fbbf24" },
    track: { bg: "rgba(99, 102, 241, 0.16)", fg: "#a5b4fc" },
    accent: { bg: "rgba(99, 102, 241, 0.16)", fg: "#a5b4fc" },
    neutral: { bg: "action.selected", fg: "text.secondary" },
};
export function Pill({ label, tone }) {
    const c = TONE_COLOR[tone];
    return (_jsx(Box, { component: "span", sx: {
            flexShrink: 0,
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1.7,
            px: 0.85,
            borderRadius: 999,
            bgcolor: c.bg,
            color: c.fg,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
        }, children: label }));
}
export function SectionLabel({ children }) {
    return (_jsx(Typography, { sx: { fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary", mb: 0.75 }, children: children }));
}
export function PageTitle({ children, action }) {
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.75 }, children: [_jsx(Typography, { sx: { fontSize: 18, fontWeight: 700 }, children: children }), action] }));
}
export function RemoteTag({ children }) {
    return (_jsxs(Typography, { component: "span", sx: {
            fontSize: 9.5,
            fontWeight: 500,
            letterSpacing: "0.02em",
            color: "text.secondary",
            bgcolor: "action.selected",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.75,
            px: 0.6,
            py: "1px",
        }, children: ["\u00B7 ", children] }));
}
export function ViewerTag({ name }) {
    return (_jsxs(Box, { sx: {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 10.5,
            fontWeight: 700,
            color: "#a5b4fc",
            bgcolor: "rgba(99, 102, 241, 0.14)",
            borderRadius: 999,
            px: 0.9,
            py: 0.25,
        }, children: ["VIEWING AS ", name.toUpperCase()] }));
}
export function Panel({ title, remote, action, children }) {
    return (_jsx(Card, { variant: "outlined", children: _jsxs(CardContent, { sx: { p: 1.5, "&:last-child": { pb: 1.5 } }, children: [_jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 0.75, mb: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flexShrink: 0 }, children: [_jsx(Typography, { sx: { fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap" }, children: title }), remote && _jsx(RemoteTag, { children: remote })] }), action] }), children] }) }));
}
export function StatTile({ value, label, tone }) {
    return (_jsxs(Box, { sx: { flex: 1, bgcolor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.1 }, children: [_jsx(Typography, { sx: { fontSize: 19, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, color: TONE_COLOR[tone].fg }, children: value }), _jsx(Typography, { sx: { fontSize: 10.5, color: "text.secondary", mt: 0.25 }, children: label })] }));
}
const ROW_ICONS = {
    car: _jsx(CarRepairOutlinedIcon, { sx: { fontSize: 16 } }),
    flame: _jsx(LocalFireDepartmentOutlinedIcon, { sx: { fontSize: 16 } }),
    droplet: _jsx(WaterDropOutlinedIcon, { sx: { fontSize: 16 } }),
    leaf: _jsx(GrassOutlinedIcon, { sx: { fontSize: 16 } }),
    snow: _jsx(AcUnitOutlinedIcon, { sx: { fontSize: 16 } }),
    clean: _jsx(CleaningServicesOutlinedIcon, { sx: { fontSize: 16 } }),
};
export function RowIcon({ name }) {
    return (_jsx(Box, { sx: { width: 28, height: 28, borderRadius: 1, bgcolor: "background.default", border: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0 }, children: ROW_ICONS[name] ?? null }));
}
export function Row({ icon, title, subtitle, right, }) {
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, py: 0.85 }, children: [icon && _jsx(RowIcon, { name: icon }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: { fontSize: 12.5, fontWeight: 600 }, children: title }), subtitle && (_jsx(Typography, { sx: { fontSize: 11, color: "text.secondary", mt: "1px" }, noWrap: true, children: subtitle }))] }), right] }));
}
export function RowGroup({ children }) {
    return _jsx(Box, { sx: { "& > *:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }, children: children });
}
export function MiniBarChart({ data, keys, colors, }) {
    const max = Math.max(1, ...data.flatMap((d) => keys.map((k) => Number(d[k]) || 0)));
    return (_jsx(Box, { sx: { display: "flex", alignItems: "flex-end", gap: 1.5, height: 110, pt: 1 }, children: data.map((d, i) => (_jsxs(Box, { sx: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }, children: [_jsx(Box, { sx: { flex: 1, display: "flex", alignItems: "flex-end", gap: "3px", width: "100%", justifyContent: "center" }, children: keys.map((k, ki) => {
                        const v = Number(d[k]) || 0;
                        return (_jsx(Box, { title: `${k}: $${v}`, sx: {
                                width: 10,
                                height: `${(v / max) * 100}%`,
                                minHeight: v > 0 ? 2 : 0,
                                borderRadius: "3px 3px 0 0",
                                bgcolor: colors[ki],
                            } }, k));
                    }) }), _jsx(Typography, { sx: { fontSize: 10, color: "text.secondary", mt: 0.5 }, children: d.week })] }, i))) }));
}
